/**
 * Where the password reset email lands.
 *
 * The Supabase client runs PKCE with detectSessionInUrl, so it exchanges the
 * ?code= in the link for a session on load; this page only has to wait for
 * that session and then call updateUser. The code verifier lives in the
 * browser that asked for the reset, so a link opened anywhere else never gets
 * a session. The page says so instead of spinning forever.
 *
 * `${origin}/reset-password` must be listed under Auth > URL Configuration >
 * Redirect URLs in the Supabase project for each environment, or Supabase
 * sends people to the Site URL instead.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { AuthShell } from "../components/AuthShell";
import { ErrorBox, Field, inputClass, primaryButtonClass } from "../components/form";

const MIN_LENGTH = 8;
const EXCHANGE_GRACE_MS = 4000;

export function ResetPassword() {
  const { supabase, session } = useSession();
  const navigate = useNavigate();

  const [gaveUp, setGaveUp] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (session) return;
    const timer = window.setTimeout(() => setGaveUp(true), EXCHANGE_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [session]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }

    setBusy(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }
    navigate("/", { replace: true });
  }

  if (!session) {
    return (
      <AuthShell>
        <h1 className="font-display text-3xl font-semibold text-ink">Reset your password</h1>
        {gaveUp ? (
          <>
            <p className="mt-3 text-stone-600">
              This link has expired, was already used, or was opened in a different browser from
              the one that requested it. Request a new link and open it on this device.
            </p>
            <Link
              to="/login"
              className="mt-8 inline-block text-sm font-medium text-leaf-700 hover:text-leaf-800"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <p className="mt-3 text-stone-600">Checking your reset link</p>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-semibold text-ink">Set a new password</h1>
      <p className="mt-2 text-stone-600">
        For {session.user.email ?? "your account"}. At least {MIN_LENGTH} characters.
      </p>

      {error ? <ErrorBox message={error} /> : null}

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <Field label="New password" htmlFor="new-password">
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Confirm new password" htmlFor="confirm-password">
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            className={inputClass}
          />
        </Field>

        <button type="submit" disabled={busy} className={primaryButtonClass}>
          {busy ? "Saving" : "Save password"}
        </button>
      </form>
    </AuthShell>
  );
}
